import { useCallback, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { ServiceGateBanner } from "./ServiceGateBanner";
import {
  fmtCents,
  ghostBtnClass,
  inputClass,
  offeringLabel,
  primaryBtnClass,
  queueLabel,
} from "./serviceShared";
import { useServiceAnchorLayerGate } from "./useServiceAnchorLayerGate";
import { useServiceLaborRatesPanel } from "./useServiceLaborRatesPanel";

export function ServiceLaborRatesSection(props: { navTick: number; onChanged?: () => void }) {
  const [newSignal, setNewSignal] = useState(0);
  const m = useServiceLaborRatesPanel(props.navTick, newSignal);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Scope labor rates by queue and/or offering. Flat = per job; hourly = rate × job duration.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={primaryBtnClass} onClick={() => setNewSignal((s) => s + 1)}>
          + New rate
        </button>
      </div>
      {m.localError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {m.localError}
        </div>
      ) : null}
      <ServiceLaborRatesEditor m={m} onSaved={props.onChanged} />
    </div>
  );
}

export function ServiceLaborRatesPanel() {
  const gate = useServiceAnchorLayerGate();
  const [navTick, setNavTick] = useState(0);
  const [newSignal, setNewSignal] = useState(0);
  const m = useServiceLaborRatesPanel(navTick, newSignal);

  const handleRefresh = useCallback(() => {
    gate.refresh();
    setNavTick((s) => s + 1);
  }, [gate]);

  return (
    <div className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Labor cost rates</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={primaryBtnClass}
            onClick={() => setNewSignal((s) => s + 1)}
            disabled={!gate.enabled}
          >
            + New rate
          </button>
          <button
            type="button"
            className={ghostBtnClass}
            onClick={handleRefresh}
            disabled={gate.loading || m.listBusy}
          >
            {m.listBusy ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <ServiceGateBanner gate={gate} />
      {gate.enabled && m.localError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {m.localError}
        </div>
      ) : null}

      {gate.enabled ? <ServiceLaborRatesEditor m={m} onSaved={handleRefresh} /> : null}
    </div>
  );
}

function ServiceLaborRatesEditor(props: {
  m: ReturnType<typeof useServiceLaborRatesPanel>;
  onSaved?: () => void;
}) {
  const { m, onSaved } = props;
  return (
    <>
          <SectionCard title="Add labor rate">
            <p className="mb-3 text-xs text-gray-500 dark:text-zinc-400">
              Scope by queue and/or offering. Leave both blank for an org-wide default. Flat = per job;
              hourly = rate × job duration (from offering or queue default).
            </p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              <input
                className={inputClass}
                placeholder="Label (optional)"
                value={m.draft.label}
                onChange={(e) => m.setDraft((d) => ({ ...d, label: e.target.value }))}
              />
              <select
                className={inputClass}
                value={m.draft.queue_id}
                onChange={(e) => m.setDraft((d) => ({ ...d, queue_id: e.target.value }))}
              >
                <option value="">Any queue</option>
                {m.queues.map((q) => (
                  <option key={q.id} value={q.id}>
                    {q.label}
                  </option>
                ))}
              </select>
              <select
                className={inputClass}
                value={m.draft.offering_id}
                onChange={(e) => m.setDraft((d) => ({ ...d, offering_id: e.target.value }))}
              >
                <option value="">Any offering</option>
                {m.offerings.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
              <input
                className={inputClass}
                placeholder="Rate (USD)"
                inputMode="decimal"
                value={m.draft.rate_dollars}
                onChange={(e) => m.setDraft((d) => ({ ...d, rate_dollars: e.target.value }))}
              />
              <select
                className={inputClass}
                value={m.draft.rate_kind}
                onChange={(e) =>
                  m.setDraft((d) => ({
                    ...d,
                    rate_kind: e.target.value as "flat" | "hourly",
                  }))
                }
              >
                <option value="flat">Flat per job</option>
                <option value="hourly">Hourly</option>
              </select>
            </div>
            <button
              type="button"
              className={`${primaryBtnClass} mt-3`}
              disabled={m.actionBusy}
              onClick={() => void m.saveRate().then(() => onSaved?.())}
            >
              {m.actionBusy ? "Saving…" : "Save rate"}
            </button>
          </SectionCard>

          <SectionCard title="Rate schedule">
            {m.rates.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-zinc-400">No labor rates configured.</p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
                {m.rates.map((rate) => (
                  <li key={rate.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                        {rate.label || "Unlabeled"}{" "}
                        {!rate.is_active ? (
                          <span className="text-xs text-gray-500">(inactive)</span>
                        ) : null}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-zinc-400">
                        {fmtCents(rate.rate_cents)}
                        {rate.rate_kind === "hourly" ? "/hr" : " flat"}
                        {" · "}
                        {queueLabel(m.queues, rate.queue_id)}
                        {" · "}
                        {offeringLabel(m.offerings, rate.offering_id)}
                      </div>
                    </div>
                    <button
                      type="button"
                      className={ghostBtnClass}
                      disabled={m.actionBusy}
                      onClick={() => void m.toggleActive(rate)}
                    >
                      {rate.is_active ? "Deactivate" : "Activate"}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
    </>
  );
}
